<script>
import { apiSecond } from '@/boot/axios'
import VueApexCharts from 'vue-apexcharts'

function extractRegionName(item) {
	return (
		item.region_name || item.region?.name || item.region?.title || item.name || item.title || ''
	)
}

function extractRegionId(item, fallbackName) {
	return item.region_id ?? item.region?.id ?? item.id ?? fallbackName
}

function extractValue(item) {
	const raw =
		item.value ??
		item.quota_value ??
		item.trade_value ??
		item.total_value ??
		item.total ??
		item.sum ??
		0
	return Number(raw) || 0
}

// Эндпоинт v1/analytics/region-monitoring отдаёт CSV (fputcsv, разделитель ';', с BOM),
// а не JSON — поэтому разбираем строку сами. Учитываем кавычки: названия регионов
// содержат пробел, а значит приходят обрамлёнными в " ".
function parseCsvRows(text, delimiter = ';') {
	const rows = []
	let row = []
	let field = ''
	let inQuotes = false

	const clean = String(text).replace(/^\uFEFF/, '')

	const pushField = () => {
		row.push(field)
		field = ''
	}
	const pushRow = () => {
		pushField()
		if (row.some((cell) => cell !== '')) rows.push(row)
		row = []
	}

	for (let i = 0; i < clean.length; i++) {
		const char = clean[i]

		if (inQuotes) {
			if (char === '"') {
				if (clean[i + 1] === '"') {
					field += '"'
					i++
				} else {
					inQuotes = false
				}
			} else {
				field += char
			}
			continue
		}

		if (char === '"') inQuotes = true
		else if (char === delimiter) pushField()
		else if (char === '\r') continue
		else if (char === '\n') pushRow()
		else field += char
	}
	pushRow()

	return rows
}

// CSV: "ID региона";"Регион";"Квоты"|"Выловы" — первая строка заголовочная.
function csvToItems(text) {
	const rows = parseCsvRows(text)
	if (rows.length < 2) return []

	return rows.slice(1).map((cells) => ({
		region_id: cells[0] ?? null,
		region_name: cells[1] ?? '',
		// в CSV дробная часть может прийти с запятой
		total_value: String(cells[2] ?? '').replace(',', '.'),
	}))
}

// Ответ бывает и массивом (JSON), и строкой (CSV) — приводим к единому виду.
function normalizeResponse(res) {
	const payload = res?.data

	if (typeof payload === 'string') return csvToItems(payload)
	if (Array.isArray(payload)) return payload
	if (Array.isArray(payload?.data)) return payload.data

	return []
}

export default {
	name: 'RegionMonitoring',
	components: {
		VueApexCharts,
	},
	data() {
		return {
			loading: true,
			regions: [],
		}
	},
	computed: {
		totalQuota() {
			return this.regions.reduce((sum, r) => sum + (r.quota || 0), 0)
		},
		totalCatch() {
			return this.regions.reduce((sum, r) => sum + (r.catchValue || 0), 0)
		},
		totalDevelopment() {
			if (!this.totalQuota) return 0
			return (this.totalCatch / this.totalQuota) * 100
		},
		sortedRegions() {
			return [...this.regions].sort((a, b) => (b.quota || 0) - (a.quota || 0))
		},
		topRegions() {
			return this.sortedRegions.slice(0, 8)
		},
		chartOptions() {
			let $this = this
			return {
				chart: {
					type: 'bar',
					height: 340,
					toolbar: { show: false },
				},
				plotOptions: {
					bar: {
						horizontal: true,
						borderRadius: 4,
						barHeight: '60%',
					},
				},
				dataLabels: { enabled: false },
				colors: ['#EB6834', '#157FEE'],
				xaxis: {
					categories: this.topRegions.map((r) => r.name),
					labels: {
						formatter: (val) => $this.$options.filters.formatNumber(Math.round(val)),
					},
				},
				legend: {
					position: 'top',
					horizontalAlign: 'left',
				},
				tooltip: {
					y: {
						formatter: (val) => $this.$options.filters.formatNumber(+val.toFixed(2)) + ' кг',
					},
				},
			}
		},
		series() {
			return [
				{
					name: this.$t('labels.quato'),
					data: this.topRegions.map((r) => r.quota || 0),
				},
				{
					name: this.$t('labels.quota_all'),
					data: this.topRegions.map((r) => r.catchValue || 0),
				},
			]
		},
	},
	mounted() {
		this.getData()
	},
	methods: {
		async getData() {
			const year = new Date().getFullYear()
			this.loading = true
			try {
				const [quotasRes, tradesRes] = await Promise.all([
					apiSecond.get('v1/analytics/region-monitoring', {
						params: { type: 'quotas', year },
					}),
					apiSecond.get('v1/analytics/region-monitoring', {
						params: { type: 'trades', year },
					}),
				])

				const quotaItems = normalizeResponse(quotasRes)
				const tradeItems = normalizeResponse(tradesRes)

				const map = new Map()

				quotaItems.forEach((item) => {
					const name = extractRegionName(item)
					if (!name) return
					const id = extractRegionId(item, name)
					map.set(id, { id, name, quota: extractValue(item), catchValue: 0 })
				})

				tradeItems.forEach((item) => {
					const name = extractRegionName(item)
					if (!name) return
					const id = extractRegionId(item, name)
					if (map.has(id)) {
						map.get(id).catchValue = extractValue(item)
					} else {
						map.set(id, { id, name, quota: null, catchValue: extractValue(item) })
					}
				})

				this.regions = Array.from(map.values())
			} catch (error) {
				console.log(error)
				this.regions = []
			} finally {
				this.loading = false
			}
		},
		developmentPercent(region) {
			if (!region.quota) return null
			return (region.catchValue / region.quota) * 100
		},
	},
}
</script>

<template>
	<div class="bg-white/90 p-[30px] shadow-main rounded-[10px] backdrop-blur-[10px]">
		<template v-if="regions.length">
			<div class="w-full flex justify-between items-center gap-[10px] mb-[24.18px]">
				<div class="font-main font-semibold text-[18px] text-third uppercase">
					{{ $t('titles.quota_catch_by_region') }}
				</div>
			</div>
			<VueApexCharts type="bar" height="340" :options="chartOptions" :series="series" />

			<div class="mt-[10px]">
				<div
					v-for="(region, regionInd) in sortedRegions"
					:key="region.id"
					class="border-b border-b-solid border-b-[#EEF1F6] py-[14px] last:border-b-0"
				>
					<div class="font-main font-semibold text-[14px] text-third mb-[8px]">
						{{ regionInd + 1 }}&nbsp; {{ region.name }}
					</div>
					<div
						class="flex flex-wrap justify-between gap-[8px] font-main text-[12px] text-second mb-[8px]"
					>
						<span
							>{{ $t('labels.quato') }}
							<b class="text-third font-semibold">{{
								region.quota
									? $options.filters.formatNumber(region.quota) + ' кг'
									: $t('titles.quota_not_set')
							}}</b></span
						>
						<span
							>{{ $t('labels.quota_all') }}
							<b class="text-third font-semibold"
								>{{ region.catchValue | formatNumber }} кг</b
							></span
						>
						<span
							>{{ $t('titles.quota_development') }}
							<span class="text-main">{{
								developmentPercent(region) !== null
									? developmentPercent(region).toFixed(1) + ' %'
									: '—'
							}}</span></span
						>
					</div>
					<div class="h-[5px] bg-[#EEF1F6] rounded-[4px] overflow-hidden">
						<div
							class="h-full bg-main"
							:style="{
								width: Math.min(developmentPercent(region) || 0, 100) + '%',
							}"
						></div>
					</div>
				</div>
			</div>
		</template>

		<div
			v-else-if="!loading"
			class="flex flex-col items-center justify-center h-[200px] text-center"
		>
			<div class="font-main font-medium text-[16px] text-fourth leading-[1.5]">
				{{ $t('titles.no_region_data') }}
			</div>
		</div>
	</div>
</template>
